/** Deep links INTO the /api-requests grid from other features, and the quick
 *  actions every value on an API-request surface offers (the detail dialog's
 *  header, Info tab and Reimport tab).
 *
 * The grid refuses windowless searches (the table is too large), so every
 * link built here carries a created window: ±1 h around a reference moment
 * when one is known (e.g. an import run's created_ts — the webhook that
 * produced the run landed moments before it), the default 7-day window
 * otherwise. Correlation hops (request id, uuid, external booking id) use
 * ±1 h around THIS request; "more like this" hops (status, type, store,
 * integration, error series) widen to ±1 day so neighbours actually show up.
 *
 * Every other href comes from gridLinks; every action from ValueActions'
 * `act`. Id spaces: `booking=` here and on /import-runs and /delivery-logs is
 * the EXTERNAL booking id the vendor sent — on /bookings that same value is
 * `id=` (the custom_id), never `gtu=` (the console pk).
 */
import { serialize, type FilterNode } from '@/components/QuerySearch';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { createdWindowNode } from '@/features/delivery-logs/createdWindow';
import {
  apiRequestsHref,
  auditHref,
  bgtasksHref,
  bookingsHref,
  cond,
  createdAroundNode,
  deliveryLogsHref,
  importRunsHref,
  ordersHref,
  subscriptionsHref,
  tourOperatorsHref,
  usersHref,
} from '@/utils/gridLinks';

// The window helper now lives with the other href builders (utils/gridLinks);
// re-exported so existing importers keep working.
export { createdAroundNode };

/** /api-requests searching one platform request id, windowed around
 *  `aroundTs` (±1 h) when given. `request` is exact-only in the registry —
 *  contract-tested — so the hardcoded operator can't drift into a 400. */
export function apiRequestsHrefForRequestId(requestId: string, aroundTs?: string | null): string {
  const nodes: FilterNode[] = [
    { kind: 'cond', namespace: 'request', operator: 'exact', value: requestId },
    aroundTs ? createdAroundNode(aroundTs) : createdWindowNode(),
  ];
  return `/api-requests?q=${encodeURIComponent(serialize(nodes))}`;
}

export interface ApiRequestCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
  /** This request's `created_ts` — the anchor of every windowed hop. */
  createdTs: string | null | undefined;
}

const tenantOf = (ctx: ApiRequestCtx) => ctx.tenantId ?? null;

/** ±`hours` around this request, or the grid's 7-day default when the
 *  request has no timestamp (never possible in practice, but the builder
 *  must not be able to emit a windowless link). */
function near(ctx: ApiRequestCtx, hours: number): { tenant: number | null; window: FilterNode } {
  return {
    tenant: tenantOf(ctx),
    window: ctx.createdTs ? createdAroundNode(ctx.createdTs, hours) : createdWindowNode(),
  };
}

// ---- Identity -------------------------------------------------------------

export function tenantActions(tenant: { id: number }, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(apiRequestsHref([], { ...near(ctx, 24), tenant: tenant.id }), 'API requests of this operator'),
    act.filter(importRunsHref([], tenant.id), 'Import runs of this operator'),
  ];
}

/** The console row id (integer pk). */
export function apiRequestIdActions(id: number, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.copy(String(id), 'Copy request row id'),
    act.filter(apiRequestsHref([cond('id', String(id), 'exact')], near(ctx, 24)), 'This request in the grid'),
  ];
}

/** The api_request_id UUID — also the label the AI diagnose task carries. */
export function uuidActions(uuid: string, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.copy(uuid, 'Copy request UUID'),
    act.filter(apiRequestsHref([cond('uuid', uuid, 'exact')], near(ctx, 1)), 'This request by UUID'),
    act.filter(
      bgtasksHref({ task_label_contains: uuid, task_type: 'import_diagnose' }),
      'Diagnose runs for this request',
    ),
  ];
}

/** The platform request id shared by everything one HTTP call produced. */
export function requestIdActions(requestId: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(requestId, 'Copy request id'),
    act.filter(importRunsHref([cond('request', requestId, 'exact')], t), 'Import runs from this request'),
    act.filter(deliveryLogsHref([cond('request', requestId)], near(ctx, 12)), 'Delivery logs from this request'),
    act.filter(
      apiRequestsHref([cond('request', requestId, 'exact')], near(ctx, 1)),
      'Sibling API requests (±1 h)',
    ),
  ];
}

/** The EXTERNAL booking id the payload carried. */
export function externalBookingIdActions(bookingId: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(bookingId, 'Copy booking id'),
    act.filter(importRunsHref([cond('booking', bookingId, 'exact')], t), 'Import runs for this booking id'),
    act.filter(bookingsHref([cond('id', bookingId)], t), 'Bookings with this custom id'),
    act.filter(ordersHref([cond('id', bookingId)], t), 'Orders containing this booking id'),
    act.filter(deliveryLogsHref([cond('booking', bookingId)], near(ctx, 24)), 'Delivery logs for this booking id'),
    act.filter(
      apiRequestsHref([cond('booking', bookingId, 'exact')], near(ctx, 1)),
      'Sibling API requests for this booking',
    ),
  ];
}

// ---- Classification -------------------------------------------------------

export function statusCodeActions(code: number, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.filter(
      apiRequestsHref([cond('status', String(code), 'exact')], near(ctx, 24)),
      `Requests answered ${code} (±1 day)`,
    ),
  ];
}

export function typeActions(label: string, ctx: ApiRequestCtx): ValueAction[] {
  return [act.filter(apiRequestsHref([cond('type', label)], near(ctx, 24)), `"${label}" requests (±1 day)`)];
}

export function storeActions(label: string, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.filter(apiRequestsHref([cond('store', label)], near(ctx, 24)), `Requests stored as "${label}" (±1 day)`),
  ];
}

export function integrationActions(codename: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(apiRequestsHref([cond('integration', codename)], near(ctx, 24)), 'Requests of this integration'),
    act.filter(subscriptionsHref([cond('restech', codename)], t), 'Webhook subscriptions of this integration'),
    act.filter(tourOperatorsHref([cond('restech', codename)]), 'Operators on this booking system'),
  ];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

/** The reimport tab's alert names the integration the replay routes through. */
export function reimportIntegrationActions(codename: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Open the operator's integrations"));
  out.push(
    act.filter(apiRequestsHref([cond('integration', codename)], near(ctx, 24)), 'Requests of this integration'),
  );
  return out;
}

export function ipActions(ip: string): ValueAction[] {
  return [act.whois(ip), act.filter(auditHref({ geo: ip }), 'Audit entries from this IP'), act.copy(ip, 'Copy IP')];
}

/** The error series stamped by a failed (re)import. `/delivery-logs?q=series=`
 *  is a DIFFERENT id space and is deliberately not offered. */
export function errorSeriesActions(seriesId: string, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.copy(seriesId, 'Copy error series id'),
    act.filter(
      apiRequestsHref([cond('error_series', seriesId, 'exact')], near(ctx, 24)),
      'Requests in this error series',
    ),
    act.filter(apiRequestsHref([cond('errors', 'yes', 'exact')], near(ctx, 24)), 'Failed requests (±1 day)'),
  ];
}

// ---- Touched records ------------------------------------------------------

export function guestEmailActions(email: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.mailto(email),
    act.copy(email, 'Copy email'),
    act.filter(bookingsHref([cond('email', email)], t), 'Bookings with this email'),
    act.filter(usersHref('guests', { tenant: t, email_contains: email }), 'Guest accounts with this email'),
  ];
}

/** `booking_contact` is free text ("Jane Doe <jane@x.y>", a bare email, or a
 *  name). Only the email part earns a mailto / grid filter. */
export function contactActions(contact: string, ctx: ApiRequestCtx): ValueAction[] {
  const email = /[^\s<>"]+@[^\s<>"]+\.[^\s<>"]+/.exec(contact)?.[0];
  if (!email) return [act.copy(contact, 'Copy contact')];
  return [act.copy(contact, 'Copy contact'), ...guestEmailActions(email, ctx)];
}

export function linkedBookingActions(b: { id: number }, ctx: ApiRequestCtx): ValueAction[] {
  return [
    act.open(`/bookings/${b.id}`, 'Open booking'),
    act.copy(String(b.id), 'Copy booking id'),
    act.filter(bookingsHref([cond('gtu', String(b.id))], tenantOf(ctx)), 'This booking in the grid'),
  ];
}

export function bookingDateActions(date: string, ctx: ApiRequestCtx): ValueAction[] {
  return [act.filter(bookingsHref([cond('date', date, 'exact')], tenantOf(ctx)), 'Bookings on this date')];
}

export function linkedOrderActions(o: { id: number }): ValueAction[] {
  return [act.open(`/orders/${o.id}`, 'Open order'), act.copy(String(o.id), 'Copy order id')];
}

export function orderRefActions(ref: string, ctx: ApiRequestCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(ref, 'Copy order ref'),
    act.filter(ordersHref([cond('order', ref)], t), 'This order in the grid'),
    act.filter(bookingsHref([cond('order', ref)], t), 'Bookings in this order'),
    act.filter(deliveryLogsHref([cond('order', ref)], near(ctx, 24)), 'Delivery logs for this order'),
  ];
}
